<template>
  <div class="order-page">
    <!-- 页面标题栏 -->
    <div class="page-header">
      <div class="back-btn" @click="$router.go(-1)">
        <i class="el-icon-arrow-left"></i>
      </div>
      <h1>我的订单</h1>
    </div>

    <!-- 筛选栏 - 已移除"等待签约"选项 -->
    <div class="filter-bar">
      <div 
        v-for="(item, index) in filterOptions" 
        :key="index"
        @click="activeFilter = index"
        :class="['filter-item', activeFilter === index ? 'active' : '']"
      >
        {{ item.text }}
      </div>
    </div>

    <!-- 订单列表区 -->
    <div class="order-list">
      <!-- 空订单状态 -->
      <div class="empty-state" v-if="filteredOrders.length === 0">
        <p class="empty-text">暂无相关订单记录</p>
      </div>

      <!-- 订单项 -->
      <div class="order-item" v-for="order in filteredOrders" :key="order.order_id">
        <!-- 订单头部：编号+状态 -->
        <div class="order-header">
          <div class="order-id">订单编号：{{ order.order_id }}</div>
          <div 
            class="order-status" 
            :class="getStatusClass(order.status.toLowerCase())"
          >
            {{ formatStatus(order.status.toLowerCase()) }}
          </div>
        </div>

        <!-- 订单主体：详情信息 -->
        <div class="order-body">
          <div class="order-info">
            <div class="info-row">
              <span class="label">会员类型：</span>
              <span class="value">{{ formatMembershipType(order.membership_type.toLowerCase()) }}</span>
            </div>
            <div class="info-row">
              <span class="label">支付方式：</span>
              <span class="value">
                {{ formatPaymentType(order.payment_type.trim()) }}
                <el-tooltip 
                  v-if="order.payment_type.includes('wechat')" 
                  content="微信支付暂不支持查看详情" 
                  placement="top"
                >
                  <i class="el-icon-info" style="margin-left: 4px; color: #999; font-size: 12px;"></i>
                </el-tooltip>
              </span>
            </div>
            <div class="info-row">
              <span class="label">创建时间：</span>
              <span class="value">{{ formatTime(order.create_time) }}</span>
            </div>
            <div class="info-row" v-if="order.complete_time">
              <span class="label">完成时间：</span>
              <span class="value">{{ formatTime(order.complete_time) }}</span>
            </div>
            <div class="info-row" v-if="order.is_renewal">
              <span class="label">续费信息：</span>
              <span class="value">父订单 {{ order.parent_order_id || '无' }}</span>
            </div>
            <!-- 修改：修复时间信息显示逻辑 -->
            <div class="info-row" v-if="shouldShowTimeInfo(order)">
              <span class="label">{{ getTimeInfoLabel(order) }}：</span>
              <span class="value">{{ getTimeInfoValue(order) }}</span>
            </div>
          </div>

          <!-- 订单金额 -->
          <div class="order-amount">
            订单金额：<span class="amount">¥{{ order.amount.toFixed(2) }}</span>
          </div>
        </div>

        <!-- 订单底部：操作按钮 - 已移除"等待签约"相关的支付按钮 -->
        <div class="order-footer">
          <button 
            class="detail-btn" 
            @click="showOrderDetail(order)"
            :disabled="order.payment_type.includes('wechat')"
          >
            {{ order.payment_type.includes('wechat') ? '暂不支持' : '查看详情' }}
          </button>
          <button 
            class="pay-btn" 
            @click="handlePayment(order)"
            v-if="order.status.toLowerCase() === 'pending'"
          >
            去支付
          </button>
        </div>
      </div>
    </div>

    <!-- 订单详情弹窗 -->
    <el-dialog 
      title="订单详情" 
      :visible.sync="detailVisible" 
      width="90%" 
      max-width="500px"
      :append-to-body="true"
    >
      <div class="detail-content" v-if="currentOrder">
        <div class="detail-item">
          <span class="detail-label">订单编号</span>
          <span class="detail-value">{{ currentOrder.order_id }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">会员类型</span>
          <span class="detail-value">{{ formatMembershipType(currentOrder.membership_type.toLowerCase()) }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">支付方式</span>
          <span class="detail-value">
            {{ formatPaymentType(currentOrder.payment_type.trim()) }}
            <span v-if="currentOrder.payment_type.includes('wechat')" class="wechat-tip">（暂不支持）</span>
          </span>
        </div>
        <div class="detail-item">
          <span class="detail-label">订单金额</span>
          <span class="detail-value">¥{{ currentOrder.amount.toFixed(2) }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">订单状态</span>
          <span 
            class="detail-value" 
            :class="getStatusClass(currentOrder.status.toLowerCase())"
          >
            {{ formatStatus(currentOrder.status.toLowerCase()) }}
          </span>
        </div>
        <div class="detail-item">
          <span class="detail-label">创建时间</span>
          <span class="detail-value">{{ formatTime(currentOrder.create_time) }}</span>
        </div>
        <div class="detail-item" v-if="currentOrder.complete_time">
          <span class="detail-label">完成时间</span>
          <span class="detail-value">{{ formatTime(currentOrder.complete_time) }}</span>
        </div>
        <!-- 修改：修复时间信息显示逻辑 -->
        <div class="detail-item" v-if="shouldShowTimeInfo(currentOrder)">
          <span class="detail-label">{{ getTimeInfoLabel(currentOrder) }}</span>
          <span class="detail-value">{{ getTimeInfoValue(currentOrder) }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">是否续费</span>
          <span class="detail-value">{{ currentOrder.is_renewal ? '是' : '否' }}</span>
        </div>
        <div class="detail-item" v-if="currentOrder.parent_order_id">
          <span class="detail-label">父订单编号</span>
          <span class="detail-value">{{ currentOrder.parent_order_id }}</span>
        </div>
      </div>
      <div slot="footer" v-if="currentOrder && currentOrder.status.toLowerCase() === 'pending'">
        <button class="pay-btn" @click="handlePayment(currentOrder)">去支付</button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { queryOrders } from '@/api/home'; 
import { mapState } from 'vuex';

export default {
  name: 'Orders',
  data() {
    return {
      orderList: [],
      userMembership: {}, // 存储用户会员信息
      activeFilter: 0,
      // 筛选选项已移除"等待签约"
      filterOptions: [
        { text: '全部订单', value: 'all' },
        { text: '已完成订单', value: 'success' },
        { text: '待支付', value: 'pending' },
        { text: '已取消', value: 'canceled' },
        { text: '已失败', value: 'failed' }
      ],
      detailVisible: false,
      currentOrder: null
    };
  },
  computed: {
    ...mapState(['theme']),
    filteredOrders() {
      // 筛选状态映射已移除"等待签约"
      const statusMap = {
        0: 'all',
        1: 'success',
        2: 'pending',
        3: 'canceled',
        4: 'failed'
      };
      const filterStatus = statusMap[this.activeFilter];
      
      if (filterStatus === 'all') return this.orderList;
      
      return this.orderList.filter(order => 
        order.status.toLowerCase() === filterStatus
      );
    }
  },
  created() {
    this.fetchOrderList();
  },
  methods: {
    async fetchOrderList() {
      try {
        const { data } = await queryOrders();
        this.orderList = data.order_info || [];
        this.userMembership = {
          is_member: data.is_member,
          current_membership: data.current_membership,
          member_expire: data.member_expire,
          days_until_expiry: data.days_until_expiry
        };
      } catch (error) {
        if (error.response?.status === 401) {
          this.$message.error('token过期，请重新登录！');
          this.$router.push('/login');
        } else {
          this.$message.error('获取订单列表失败，请稍后重试');
        }
        console.error('订单列表请求错误：', error);
      }
    },

    formatMembershipType(type) {
      const typeMap = {
        'monthly': '月度会员',
        'quarterly': '季度会员',
        'annual': '年度会员',
        'monthly_continuous': '连续包月会员',
        'quarterly_continuous': '连续包季会员'
      };
      return typeMap[type] || type;
    },

    formatPaymentType(type) {
      const typeMap = {
        'alipay_pc': '支付宝（电脑端）',
        'alipay_mobile': '支付宝（移动端）',
        'wechat_pc': '微信支付（电脑端）',
        'wechat_mobile': '微信支付（移动端）',
        'unionpay': '银联支付'
      };
      return typeMap[type] || type;
    },

    // 状态格式化已移除"等待签约"
    formatStatus(status) {
      const statusMap = {
        'pending': '待支付',
        'success': '支付成功',
        'failed': '支付失败',
        'canceled': '已取消'
      };
      return statusMap[status] || status;
    },

    // 状态样式映射已移除"等待签约"
    getStatusClass(status) {
      const classMap = {
        'pending': 'pending',
        'success': 'success',
        'failed': 'failed',
        'canceled': 'canceled'
      };
      return classMap[status] || '';
    },

    formatTime(timeStr) {
      if (!timeStr) return '';
      const date = new Date(timeStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },

    // 修复：判断是否显示时间信息（仅支付成功订单）
    shouldShowTimeInfo(order) {
      return order.status.toLowerCase() === 'success' && this.userMembership.member_expire;
    },

    // 修复：获取时间信息标签（根据订单的会员类型显示到期时间/续费时间）
    getTimeInfoLabel(order) {
      const type = order.membership_type.toLowerCase();
      if (['monthly', 'quarterly', 'annual'].includes(type)) {
        return '到期时间';
      } else if (['monthly_continuous', 'quarterly_continuous'].includes(type)) {
        return '续费时间';
      }
      return '';
    },

    // 修复：使用接口返回的member_expire时间值
    getTimeInfoValue(order) {
      return this.formatTime(this.userMembership.member_expire);
    },

    showOrderDetail(order) {
      this.currentOrder = order;
      this.detailVisible = true;
    },

    handlePayment(order) {
      this.$router.push(`/pay?orderId=${order.order_id}`);
    }
  }
};
</script>

<style lang="scss" scoped>
/* 样式部分保持不变 */
.order-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-bottom: 40px;
  display: flex;
  flex-direction: column;

  .page-header {
    display: flex;
    align-items: center;
    padding: 16px 20px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    .back-btn {
      font-size: 20px;
      color: #666;
      cursor: pointer;
      margin-right: 16px;

      &:hover {
        color: #409eff;
      }
    }

    h1 {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
  }

  .filter-bar {
    display: flex;
    padding: 12px 20px;
    background-color: #fff;
    margin-bottom: 16px;
    overflow-x: auto;
    padding-bottom: 8px;

    .filter-item {
      flex-shrink: 0;
      padding: 6px 16px;
      margin-right: 12px;
      border-radius: 20px;
      font-size: 14px;
      color: #666;
      cursor: pointer;
      transition: all 0.2s ease;

      &.active {
        background-color: #409eff;
        color: #fff !important;
      }

      &:hover:not(.active) {
        background-color: #f0f7ff;
        color: #409eff;
      }
    }
  }

  .order-list {
    padding: 0 20px;
    max-height: calc(100vh - 180px);
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #ddd #f5f7fa;
    
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 80px 0;
      color: #999;

      .empty-text {
        font-size: 14px;
      }
    }

    .order-item {
      background-color: #fff;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      transition: box-shadow 0.2s ease;

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      .order-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px dashed #f0f0f0;

        .order-id {
          font-size: 13px;
          color: #999;
        }

        .order-status {
          font-size: 14px;
          font-weight: 500;
          padding: 2px 8px;
          border-radius: 4px;

          &.success {
            color: #67c23a;
            background-color: #f0f9eb;
          }
          &.pending {
            color: #e6a23c;
            background-color: #fdf6ec;
          }
          &.failed {
            color: #f56c6c;
            background-color: #fef0f0;
          }
          &.canceled {
            color: #909399;
            background-color: #f5f5f5;
          }
        }
      }

      .order-body {
        display: flex;
        justify-content: space-between;
        margin-bottom: 16px;

        @media (max-width: 768px) {
          .order-list {
          max-height: calc(100vh - 160px);
          padding: 0 12px;
          }
          flex-direction: column;

          .order-amount {
            margin-top: 12px;
            text-align: left;
          }
        }

        .order-info {
          flex: 1;

          .info-row {
            margin-bottom: 8px;
            font-size: 14px;

            .label {
              color: #999;
              display: inline-block;
              width: 80px;
            }

            .value {
              color: #333;
            }
          }
        }

        .order-amount {
          min-width: 120px;
          text-align: right;
          font-size: 14px;
          color: #333;

          .amount {
            font-size: 16px;
            font-weight: 600;
            color: #f56c6c;
          }
        }
      }

      .order-footer {
        text-align: right;

        .detail-btn {
          padding: 6px 16px;
          border: 1px solid #409eff;
          border-radius: 4px;
          background-color: transparent;
          color: #409eff;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 10px;

          &:hover:not(:disabled) {
            background-color: #409eff;
            color: #fff;
          }

          &:disabled {
            border-color: #c0c4cc;
            color: #c0c4cc;
            cursor: not-allowed;
            background-color: #f5f5f5;
          }
        }

        .pay-btn {
          padding: 6px 16px;
          border: 1px solid #67c23a;
          background-color: #67c23a;
          color: #fff;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background-color: #52c41a;
            border-color: #52c41a;
          }
        }
      }
    }
  }

  .detail-content {
    .detail-item {
      display: flex;
      padding: 12px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      .detail-label {
          width: 100px;
          min-width: 100px;
          color: #999;
          font-size: 14px;
          text-align: justify;
          text-justify: distribute-all-lines;
          padding-right: 16px;
      }

      .detail-value {
        flex: 1;
        font-size: 14px;
        color: #333;

        .wechat-tip {
          color: #f56c6c;
          font-size: 12px;
          margin-left: 4px;
        }
      }
    }
  }
}

::v-deep .theme-dark {
    .order-list {
      scrollbar-color: #444 #2c2c2c;
    }
  .order-page {
    background-color: #1d1d1d;

    .page-header,
    .filter-bar,
    .order-item {
      background-color: #2c2c2c;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .page-header .back-btn {
      color: #ccc;

      &:hover {
        color: #409eff;
      }
    }

    h1,
    .order-info .value,
    .order-amount,
    .detail-content .detail-value {
      color: #e0e0e0;
    }

    .filter-item {
      color: #ccc;

      &:hover:not(.active) {
        background-color: #3a3a3a;
        color: #409eff;
      }
    }

    .order-id,
    .info-row .label,
    .detail-content .detail-label {
      color: #aaa;
    }

    .empty-text {
      color: #aaa;
    }
  }
}
</style>